
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { LogOut, Settings, MessageSquare, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import FeedbackDialog from "../feedback/FeedbackDialog";

const UserMenu = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/auth");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const getInitials = (email: string) => {
    return email.substring(0, 2).toUpperCase();
  };

  if (!user) {
    return (
      <Button asChild variant="default" size="sm" className="bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-semibold px-6 transition-all duration-300 hover:scale-105">
        <Link to="/auth" className="whitespace-nowrap">
          Join Now! 🚀
        </Link>
      </Button>
    );
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className="relative h-10 w-10 rounded-full ring-2 ring-primary/20 hover:ring-primary/40 transition-all duration-300"
            aria-label="User menu"
          >
            <Avatar className="h-10 w-10 cursor-pointer">
              <AvatarImage
                src={user.user_metadata?.avatar_url}
                alt="User avatar"
              />
              <AvatarFallback className="bg-gradient-to-r from-primary/20 to-primary/10 text-primary font-semibold">
                {getInitials(user.email || "")}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-card/90 backdrop-blur-sm border-primary/20">
          <DropdownMenuLabel>
            <div className="font-normal text-xs text-muted-foreground">
              Welcome back!
            </div>
            <div className="truncate text-primary font-semibold">{user.email}</div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-primary/20" />
          <DropdownMenuItem asChild>
            <Link to="/dashboard" className="flex items-center cursor-pointer hover:bg-primary/10">
              <Settings size={16} className="mr-2 text-primary" />
              Dashboard
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/profile" className="flex items-center cursor-pointer hover:bg-primary/10">
              <User size={16} className="mr-2 text-primary" />
              Profile
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setFeedbackOpen(true)} className="cursor-pointer hover:bg-primary/10">
            <MessageSquare size={16} className="mr-2 text-primary" />
            Provide Feedback
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-primary/20" />
          <DropdownMenuItem
            onClick={handleSignOut}
            className="cursor-pointer text-red-500 focus:text-red-500 dark:text-red-400 dark:focus:text-red-400 hover:bg-red-50 dark:hover:bg-red-950"
          >
            <LogOut size={16} className="mr-2" />
            Sign Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </>
  );
};

export default UserMenu;
